import { Config } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"

// Custom Auth class that uses /authen endpoints instead of /auth
export class CustomAuth {
  private client: any
  private config: Config

  constructor(client: any, config: Config) {
    this.client = client
    this.config = config
  }

  /**
   * This method is used to retrieve a registration JWT token for a user, customer, or custom actor type.
   */
  register = async (
    actor: string,
    method: string,
    payload: HttpTypes.AdminSignUpWithEmailPassword
  ): Promise<string> => {
    const { token } = await this.client.fetch(
      `/authen/${actor}/${method}/register`,
      {
        method: "POST",
        body: payload,
      }
    )
    this.client.setToken(token)
    return token
  }

  /**
   * This method retrieves the JWT authenticated token for an admin user, customer, or custom actor type.
   */
  login = async (
    actor: string,
    method: string,
    payload: HttpTypes.AdminSignInWithEmailPassword | Record<string, unknown>
  ): Promise<string | { location: string }> => {
    // There will either be token or location returned from the backend.
    const { token, location } = await this.client.fetch(
      `/authen/${actor}/${method}`,
      {
        method: "POST",
        body: payload,
      }
    )
    // In the case of an oauth login, we return the redirect location to the caller.
    if (location) {
      return { location }
    }
    await this.setToken_(token)
    return token
  }

  /**
   * This method is used to validate an Oauth callback from a third-party service.
   */
  callback = async (
    actor: string,
    method: string,
    query?: Record<string, unknown>
  ): Promise<string> => {
    const { token } = await this.client.fetch(
      `/authen/${actor}/${method}/callback`,
      {
        method: "GET",
        query,
      }
    )
    await this.setToken_(token)
    return token
  }

  /**
   * This method refreshes a JWT authentication token.
   */
  refresh = async (): Promise<string> => {
    const { token } = await this.client.fetch("/authen/token/refresh", {
      method: "POST",
    })
    await this.setToken_(token)
    return token
  }

  /**
   * This method deletes the authentication session of the currently logged-in user to log them out.
   */
  logout = async (): Promise<void> => {
    if (this.config?.auth?.type === "session") {
      await this.client.fetch("/authen/session", {
        method: "DELETE",
      })
    }
    this.client.clearToken()
  }

  /**
   * This method requests a reset password token for an admin user, customer, or custom actor type.
   */
  resetPassword = async (
    actor: string,
    provider: string,
    body: { identifier: string }
  ): Promise<void> => {
    await this.client.fetch(`/authen/${actor}/${provider}/reset-password`, {
      method: "POST",
      body,
      headers: { accept: "text/plain" }, // 201 Created response
    })
  }

  /**
   * This method is used to update user-related data authentication data.
   */
  updateProvider = async (
    actor: string,
    provider: string,
    body: Record<string, unknown>,
    token: string
  ): Promise<void> => {
    await this.client.fetch(
      `/authen/${actor}/${provider}/update?token=${token}`,
      {
        method: "POST",
        body,
      }
    )
  }

  /**
   * @ignore
   */
  private setToken_ = async (token: string): Promise<void> => {
    // By default we just set the token in the configured storage, if configured to use sessions we convert it into session storage instead.
    if (this.config?.auth?.type === "session") {
      await this.client.fetch("/authen/session", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
    } else {
      this.client.setToken(token)
    }
  }
}
